/**
 * Integrytas Group — Record Extensions: EDIT-MODAL state + save.
 *
 * Holds the form state for every extension registered against a host page, and
 * persists it through the Common Routine (§2 — routedInsert/Update/Delete, never
 * a direct table write).
 *
 * Save ordering: the host saves its OWN record first, then awaits
 * `save(session, pk)`. That ordering is what lets a brand-new record work at all
 * — the sister row is keyed by the host's pk, which does not exist until the host
 * insert returns. The two writes are therefore sequential, NOT atomic: if the
 * extension write fails the host record is already saved and the error surfaces
 * to the user, who can correct and re-save. Making it one transaction would mean
 * a single Common Routine payload spanning both tables; revisit if the pairing
 * ever needs to be all-or-nothing.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useActiveLocale } from '../lib/use_active_locale';
import { routedDelete, routedInsert, routedUpdate } from '../lib/routed_writes';
import { getRecordExtensions, type RecordExtension } from '../lib/record_extensions';
import {
  loadExtensionOptions,
  loadExtensionRecord,
  type ExtensionAddressRow,
} from '../lib/record_extension_data';
import type { AuthSession } from '../types/auth';

/** ext.key → field.key → current value. */
type ValueMap = Record<string, Record<string, unknown>>;
/** `${ext.key}:${child.key}` → address rows. */
type AddressMap = Record<string, ExtensionAddressRow[]>;

export interface UseRecordExtensionForm {
  extensions: RecordExtension[];
  values: ValueMap;
  setValue: (extKey: string, fieldKey: string, value: unknown) => void;
  addresses: AddressMap;
  setAddresses: (extKey: string, childKey: string, rows: ExtensionAddressRow[]) => void;
  /** ext.key → field.key → (raw value → label), for lookup/fk selects. */
  options: Record<string, Record<string, Record<string, string>>>;
  /** True while this host record has no sister row yet (drives immutability). */
  isNew: (extKey: string) => boolean;
  /** Persist every extension. Throws on the first failure. */
  save: (session: AuthSession, pk: string | number) => Promise<void>;
  loading: boolean;
}

const addrKey = (extKey: string, childKey: string) => `${extKey}:${childKey}`;

/** Blank-out empties so an untouched optional column is stored NULL, not ''. */
function normalize(value: unknown): unknown {
  return value === '' ? null : value;
}

export function useRecordExtensionForm(
  pageCode: string,
  pk: string | number | null,
): UseRecordExtensionForm {
  const locale = useActiveLocale();
  const extensions = useMemo(() => getRecordExtensions(pageCode), [pageCode]);
  const [values, setValues] = useState<ValueMap>({});
  const [addresses, setAddressMap] = useState<AddressMap>({});
  const [options, setOptions] = useState<Record<string, Record<string, Record<string, string>>>>({});
  const [existing, setExisting] = useState<Record<string, boolean>>({});
  const [originalAddressPks, setOriginalAddressPks] = useState<Record<string, number[]>>({});
  const [loading, setLoading] = useState(false);

  // Select options for lookup/fk fields — independent of which record is open.
  useEffect(() => {
    if (extensions.length === 0) return;
    let cancelled = false;
    void (async () => {
      const loaded = await Promise.all(
        extensions.map(async e => [e.key, await loadExtensionOptions(e, locale)] as const),
      );
      if (!cancelled) setOptions(Object.fromEntries(loaded));
    })();
    return () => { cancelled = true; };
  }, [extensions, locale]);

  // The open record's sister row + addresses.
  useEffect(() => {
    if (extensions.length === 0 || pk == null) {
      setValues({}); setAddressMap({}); setExisting({}); setOriginalAddressPks({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const loaded = await Promise.all(
        extensions.map(async e => [e, await loadExtensionRecord(e, pk)] as const),
      );
      if (cancelled) return;
      const nextValues: ValueMap = {};
      const nextAddresses: AddressMap = {};
      const nextExisting: Record<string, boolean> = {};
      const nextPks: Record<string, number[]> = {};
      for (const [ext, data] of loaded) {
        nextExisting[ext.key] = data.row !== null;
        nextValues[ext.key] = Object.fromEntries(
          ext.fields.map(f => [f.key, data.row?.[f.key] ?? '']),
        );
        for (const [childKey, rows] of Object.entries(data.addresses)) {
          nextAddresses[addrKey(ext.key, childKey)] = rows;
          nextPks[addrKey(ext.key, childKey)] = rows
            .map(r => r.__pk)
            .filter((x): x is number => x != null);
        }
      }
      setValues(nextValues);
      setAddressMap(nextAddresses);
      setExisting(nextExisting);
      setOriginalAddressPks(nextPks);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [extensions, pk]);

  const setValue = useCallback((extKey: string, fieldKey: string, value: unknown) => {
    setValues(prev => ({ ...prev, [extKey]: { ...(prev[extKey] ?? {}), [fieldKey]: value } }));
  }, []);

  const setAddresses = useCallback((extKey: string, childKey: string, rows: ExtensionAddressRow[]) => {
    setAddressMap(prev => ({ ...prev, [addrKey(extKey, childKey)]: rows }));
  }, []);

  const isNew = useCallback((extKey: string) => existing[extKey] !== true, [existing]);

  const saveAddresses = useCallback(async (
    session: AuthSession,
    ext: RecordExtension,
    pkValue: string | number,
  ) => {
    const addressChildren = (ext.children ?? []).filter(c => c.kind === 'address');
    for (const child of addressChildren) {
      if (child.kind !== 'address') continue;
      const key = addrKey(ext.key, child.key);
      // Replace-all-on-save: each existing row is removed by its own PK through
      // the Common Routine (§2 — no filter-based direct delete), then re-inserted.
      for (const rowPk of originalAddressPks[key] ?? []) {
        const { error } = await routedDelete(session, child.table, child.pk, rowPk);
        if (error) throw new Error(error.message);
      }
      for (const row of (addresses[key] ?? []).filter(r => r.line1?.trim())) {
        const { __pk: _omit, ...rest } = row;
        const { error } = await routedInsert(session, child.table, { [child.fk]: pkValue, ...rest });
        if (error) throw new Error(error.message);
      }
    }
  }, [addresses, originalAddressPks]);

  const save = useCallback(async (session: AuthSession, pkValue: string | number) => {
    for (const ext of extensions) {
      const patch = Object.fromEntries(
        ext.fields.map(f => [f.key, normalize(values[ext.key]?.[f.key])]),
      );
      const hasAnyValue = Object.values(patch).some(v => v !== null && v !== undefined);

      if (existing[ext.key]) {
        // An immutable business reference is set once and never re-keyed (§17.B).
        const updatable = Object.fromEntries(
          ext.fields.filter(f => !f.immutable_after_create).map(f => [f.key, patch[f.key]]),
        );
        if (Object.keys(updatable).length > 0) {
          const { error } = await routedUpdate(session, ext.sister.table, ext.sister.pk, pkValue, updatable);
          if (error) throw new Error(error.message);
        }
      } else if (hasAnyValue) {
        // No sister row yet: create one only when the user actually filled something
        // in, so a plain host record stays plain (an app's extension is optional).
        const { error } = await routedInsert(session, ext.sister.table, { [ext.sister.pk]: pkValue, ...patch });
        if (error) throw new Error(error.message);
      }

      await saveAddresses(session, ext, pkValue);
    }
  }, [extensions, values, existing, saveAddresses]);

  return { extensions, values, setValue, addresses, setAddresses, options, isNew, save, loading };
}
